import { useMemo, useState } from "react";
import type { PlantSummary, VolumeBucket } from "../../shared/types";
import { VOLUME_BUCKETS } from "../../shared/types";
import { logWatering, updateWatering } from "../api";
import { volumeLabel } from "../labels";

const METHOD_CHIPS = ["Top water until drains", "Sink soak", "Shower rinse", "Misted leaves"];

function toDatetimeLocalValue(ms: number) {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string) {
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function WaterSheet(props: {
  plant: PlantSummary;
  editEvent?: {
    id: string;
    occurred_at: number;
    volume_bucket: VolumeBucket;
    method_text: string;
  };
  onClose: () => void;
  onLogged: () => void | Promise<void>;
}) {
  const editing = props.editEvent != null;
  const last = editing ? null : props.plant.last_watering;
  const [volume, setVolume] = useState<VolumeBucket>(props.editEvent?.volume_bucket ?? last?.volume_bucket ?? "medium");
  const [mode, setMode] = useState<"same_as_last" | "custom">(editing || !last ? "custom" : "same_as_last");
  const [methodText, setMethodText] = useState(props.editEvent?.method_text ?? last?.method_text ?? "");
  const [occurredAt, setOccurredAt] = useState(() =>
    toDatetimeLocalValue(props.editEvent?.occurred_at ?? Date.now()),
  );
  const [copyVolume, setCopyVolume] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = useMemo(() => {
    if (mode === "same_as_last" && last) {
      return {
        method: last.method_text,
        volume: copyVolume ? last.volume_bucket : volume,
      };
    }
    return { method: methodText, volume };
  }, [copyVolume, last, methodText, mode, volume]);

  return (
    <div
      className="sheet-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="water-title">
        <h2 id="water-title">{editing ? "Edit watering" : `Water ${props.plant.name}`}</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          {editing
            ? "Fix volume, method, or date if you logged this incorrectly."
            : "Rough volume + how you watered. You can repeat your last method in one tap."}
        </p>

        {editing ? (
          <div className="field">
            <label htmlFor="occurred-at">When</label>
            <input
              id="occurred-at"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
            />
          </div>
        ) : null}

        <div className="field">
          <span id="vol-label">Volume</span>
          <div className="chips" style={{ marginTop: 8 }} role="group" aria-labelledby="vol-label">
            {VOLUME_BUCKETS.map((v) => (
              <button
                key={v}
                type="button"
                className="chip"
                aria-pressed={volume === v}
                onClick={() => setVolume(v)}
                disabled={mode === "same_as_last" && copyVolume}
              >
                {volumeLabel(v)}
              </button>
            ))}
          </div>
        </div>

        {last ? (
          <div className="field">
            <span id="how-label">How</span>
            <div className="stack" style={{ marginTop: 8 }} role="radiogroup" aria-labelledby="how-label">
              <label className="muted" style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <input
                  type="radio"
                  name="mode"
                  checked={mode === "same_as_last"}
                  onChange={() => setMode("same_as_last")}
                />
                <span>
                  <strong>Same as last time</strong>
                  <div style={{ marginTop: 6, color: "var(--text)" }}>{last.method_text}</div>
                  <label style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
                    <input type="checkbox" checked={copyVolume} onChange={(e) => setCopyVolume(e.target.checked)} />
                    Also use last volume ({volumeLabel(last.volume_bucket)})
                  </label>
                </span>
              </label>
              <label className="muted" style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <input type="radio" name="mode" checked={mode === "custom"} onChange={() => setMode("custom")} />
                <span>
                  <strong>Different method</strong>
                </span>
              </label>
            </div>
          </div>
        ) : null}

        {(editing || !last || mode === "custom") && (
          <div className="field">
            <label htmlFor="method">{editing ? "How you watered" : "Describe how you watered"}</label>
            <textarea
              id="method"
              value={methodText}
              onChange={(e) => setMethodText(e.target.value)}
              placeholder="e.g. Put in sink, soaked through, drained, soaked again after 10 minutes."
            />
            <div className="chips" style={{ marginTop: 8 }}>
              {METHOD_CHIPS.map((t) => (
                <button key={t} type="button" className="chip" onClick={() => setMethodText((prev) => (prev ? `${prev}; ${t}` : t))}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {error ? (
          <p role="alert" style={{ color: "var(--danger)" }}>
            {error}
          </p>
        ) : null}

        <div className="row" style={{ marginTop: 14 }}>
          <button type="button" className="btn btn-ghost" onClick={props.onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                if (editing && props.editEvent) {
                  const occurredMs = fromDatetimeLocalValue(occurredAt);
                  if (occurredMs == null) {
                    setError("Invalid date");
                    return;
                  }
                  await updateWatering(props.plant.id, props.editEvent.id, {
                    volume_bucket: volume,
                    method_text: methodText.trim(),
                    occurred_at: occurredMs,
                  });
                } else {
                  await logWatering(props.plant.id, {
                    volume_bucket: preview.volume,
                    method_mode: mode,
                    method_text: mode === "custom" ? methodText : undefined,
                    copy_volume_from_last: mode === "same_as_last" ? copyVolume : false,
                  });
                }
                await props.onLogged();
              } catch (e) {
                setError(e instanceof Error ? e.message : editing ? "Failed to save" : "Failed to log");
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Saving…" : editing ? "Save changes" : "Save watering"}
          </button>
        </div>
      </div>
    </div>
  );
}
